import { displayDestination } from "../lib/urls"

// "Your recent code": the newest code this browser made, offered above the
// hero form after a reload or a return visit (issue #264).
//
// Without it the homepage comes back as an empty form. Nothing shows the code
// the visitor made a minute ago, so the likely next step is a second code for
// the same link, without the logo and colors the first one had.
//
// `code` comes from GET /api/v1/qr_codes/recent, which reads the httponly
// creation-session cookie. That cookie is already this browser's proof that it
// made the code, so the card needs no token in the URL and no sign-in.
//
// A card, not an auto-open. The visitor may be here to make a different code,
// so the workspace only comes back when they ask for it. All state lives in
// QrBuilder; this component is presentation.

const openBtn =
  "inline-flex flex-shrink-0 items-center justify-center gap-1.5 min-h-[44px] px-3 sm:px-3.5 rounded border " +
  "border-[color:var(--color-border)] dark:border-storm text-coal dark:text-cloud text-sm font-semibold " +
  "transition hover:border-voltage hover:text-voltage " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 " +
  "disabled:opacity-60 disabled:cursor-not-allowed"

const hideBtn =
  "inline-flex flex-shrink-0 items-center justify-center min-h-[44px] w-7 sm:w-8 rounded " +
  "text-[color:var(--color-muted)] dark:text-muted-light transition hover:text-coal dark:hover:text-cloud " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"

export default function RecentCode({ code, onOpen, onHide, disabled = false }) {
  return (
    // More air below on a phone, where the form under it is full-bleed (no
    // card of its own) and the headline would otherwise sit against this border.
    <section
      aria-labelledby="recent-code-heading"
      className="mb-6 sm:mb-4 flex items-center gap-2 sm:gap-3 rounded-lg border border-[color:var(--color-border)] dark:border-storm bg-white dark:bg-midnight p-3"
    >
      {/* Decorative: the destination beside it is what names the code. On a
          white tile in both themes, because that is what the code prints on. */}
      <img
        src={code.image_url}
        alt=""
        width="48"
        height="48"
        className="h-12 w-12 flex-shrink-0 rounded border border-[color:var(--color-border)] dark:border-storm bg-white object-contain p-0.5"
      />
      <div className="min-w-0 flex-1">
        <h2 id="recent-code-heading" className="truncate text-sm font-semibold text-coal dark:text-cloud">
          Your recent code
        </h2>
        {/* A hosted contact page (#332): its address is a random id, so say
            what it is instead. The endpoint sends the boolean, no fields. */}
        {code.contact_page ? (
          <p className="mt-0.5 truncate text-sm text-[color:var(--color-muted)] dark:text-muted-light">
            Opens your contact page
          </p>
        ) : (
          <p
            className="mt-0.5 truncate font-mono text-sm text-[color:var(--color-muted)] dark:text-muted-light"
            title={code.destination_url}
          >
            {displayDestination(code.destination_url)}
          </p>
        )}
      </div>
      <button type="button" onClick={onOpen} disabled={disabled} className={openBtn}>
        Open code
        {/* The arrow gives way first on a narrow phone, so the heading keeps its room. */}
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="hidden sm:block">
          <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
      {/* For the person who is here to make a different code, or who is not
          the one who made this one. Hides the card for this tab session only;
          the code and the cookie that proves who made it are not touched. */}
      {onHide && (
        <button type="button" onClick={onHide} aria-label="Hide your recent code" className={hideBtn}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </section>
  )
}
